import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom";
import Logo from '../../assets/img/marroccoart-bg.png';
import { useWindowWidth } from '../../utils/useWidth';
import { FaCartShopping } from "react-icons/fa6";
import { MdOutlineAdminPanelSettings } from "react-icons/md";
import { FaRegUser } from "react-icons/fa";
import { HiMenuAlt3 } from "react-icons/hi";
import { IoClose } from "react-icons/io5";
import Cookies from 'js-cookie';
import { useCart } from '../../context/CartContext';
import { motion, AnimatePresence } from "framer-motion";

const buttons = [
  {
    id: 1,
    name: "Home",
    link: "/",
    type: "primary",
  },
  {
    id: 2,
    name: "Contatti",
    link: "/contatti",
    type: "primary",
  },
  {
    id: 3,
    name: "Chi siamo",
    link: "/chi-siamo",
    type: "primary",
  },
  {
    id: 4,
    name: "Online Shop",
    link: "/store",
    type: "primary",
  },
  {
    id: 5,
    name: "Eventi",
    link: "/eventi",
    type: "primary",
  },
  {
    id: 6,
    name: "Chiamaci Ora",
    link: "tel:+3423971162",
    type: "gg",
  }
];

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { cart } = useCart();
  const width = useWindowWidth();
  const breakpoint = 1090; // Define your breakpoint in pixels
  const isAdminLoggedIn = Cookies.get('adminKey') !== undefined;
  const isUserLoggedIn = Cookies.get('accessToken') !== undefined;

  // Animation variants
  const navbarVariants = {
    hidden: { 
      y: -100,
      opacity: 0 
    },
    visible: { 
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };
  
  const menuItemVariants = {
    hidden: { 
      x: 20, 
      opacity: 0 
    },
    visible: (i) => ({ 
      x: 0, 
      opacity: 1, 
      transition: { 
        delay: i * 0.1,
        duration: 0.4
      } 
    }),
    hover: { 
      scale: 1.05, 
      transition: { duration: 0.2 } 
    },
    tap: { 
      scale: 0.95 
    }
  };
  
  const iconButtonVariants = {
    hover: { scale: 1.1 },
    tap: { scale: 0.9 }
  };
  
  const mobileNavVariants = {
    closed: { 
      x: "100%",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30
      }
    },
    open: { 
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.07,
        delayChildren: 0.2
      }
    }
  };
  
  const mobileItemVariants = {
    closed: { opacity: 0, y: 20 },
    open: { opacity: 1, y: 0 }
  };
  
  const cartBadgeVariants = {
    initial: { scale: 0 },
    animate: { 
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 500,
        damping: 15
      }
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setIsScrolled(isScrolled);
    };

    document.addEventListener('scroll', handleScroll);
    return () => {
      document.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Close mobile menu on breakpoint change
  useEffect(() => {
    if (width >= breakpoint) {
      setIsOpen(false);
    }
  }, [width, breakpoint]);

  const totalQuantity = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <motion.header
      variants={navbarVariants}
      className={`fixed top-0 left-0 w-full z-40 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg py-1' 
          : 'bg-white shadow-md py-2'
      }`}
    >
      <div className="max-w-[100rem] mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8">
        <motion.div 
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="logo"
        >
          <Link to={"/"} className="inline-flex items-center space-x-2 py-1">
            <img src={Logo} alt="Logo" className="h-12 w-auto" />
          </Link>
        </motion.div>
        
        {/* Mobile Menu Button */}
        {width < breakpoint && (
          <motion.button
            variants={iconButtonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={() => setIsOpen(!isOpen)}
            className="relative z-20 p-2 text-gray-700 focus:outline-none"
            aria-label="Toggle Menu"
          >
            {isOpen ? (
              <IoClose size={24} />
            ) : (
              <HiMenuAlt3 size={24} />
            )}
          </motion.button>
        )}

        {/* Desktop Navigation */}
        {width >= breakpoint && (
          <nav className="flex items-center space-x-6">
            {/* Primary navigation items */}
            <div className="flex items-center space-x-5 font-medium text-[15px]">
              {buttons.map((button, i) => (
                <motion.div 
                  key={button.id} 
                  custom={i} 
                  variants={menuItemVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  {button.type === "primary" ? (
                    button.link.startsWith('#') ? (
                      <a 
                        href={button.link} 
                        className="relative overflow-hidden group py-2"
                      >
                        <span>{button.name}</span>
                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-[#406580] origin-left transform scale-x-0 transition-transform group-hover:scale-x-100"></span>
                      </a>
                    ) : (
                      <Link 
                        to={button.link} 
                        className="relative overflow-hidden group py-2"
                      >
                        <span>{button.name}</span>
                        <span className="absolute inset-x-0 bottom-0 h-0.5 bg-[#406580] origin-left transform scale-x-0 transition-transform group-hover:scale-x-100"></span>
                      </Link>
                    )
                  ) : (
                    button.link.startsWith('#') ? (
                      <a 
                        href={button.link}
                        className="bg-gradient-to-r from-[#00a7ec] to-[#006eec] text-white px-4 py-2 rounded-full hover:shadow-md transition-shadow"
                      >
                        {button.name}
                      </a>
                    ) : (
                      <Link 
                        to={button.link}
                        className="bg-gradient-to-r from-[#00a7ec] to-[#006eec] text-white px-4 py-2 rounded-full hover:shadow-md transition-shadow"
                      >
                        {button.name}
                      </Link>
                    )
                  )}
                </motion.div>
              ))}
            </div>

            <div className="h-6 w-px bg-gray-300"></div>

            {/* Auth and utilities */}
            <div className="flex items-center space-x-5">
              {!isUserLoggedIn ? (
                <>
                  <motion.div 
                    variants={menuItemVariants}
                    custom={buttons.length}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    <Link 
                      to="/login" 
                      className="relative overflow-hidden group py-2"
                    >
                      <span>Login</span>
                      <span className="absolute inset-x-0 bottom-0 h-0.5 bg-[#406580] origin-left transform scale-x-0 transition-transform group-hover:scale-x-100"></span>
                    </Link>
                  </motion.div>
                  
                  <motion.div 
                    variants={menuItemVariants}
                    custom={buttons.length + 1}
                    whileHover="hover"
                    whileTap="tap"
                  >
                    <Link 
                      to="/register" 
                      className="bg-[#406580] hover:bg-[#305570] text-white px-4 py-1.5 rounded-lg transition-colors"
                    >
                      Register
                    </Link>
                  </motion.div>
                </>
              ) : (
                <motion.div 
                  variants={iconButtonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Link 
                    to="/profilo" 
                    className="p-2 text-gray-700 hover:text-[#406580] transition-colors"
                    aria-label="Profile"
                  >
                    <FaRegUser className="text-xl" />
                  </Link>
                </motion.div>
              )}

              {/* Cart icon */}
              {cart && cart.length > 0 && (
                <motion.div 
                  variants={iconButtonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Link 
                    to="/cart" 
                    className="relative p-2 text-gray-700 hover:text-[#406580] transition-colors"
                    aria-label="Shopping cart"
                  >
                    <FaCartShopping className="text-xl" />
                    <motion.span 
                      className="absolute bottom-11 -right-5 flex items-center justify-center w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full"
                      variants={cartBadgeVariants}
                      initial="initial"
                      animate="animate"
                      key={totalQuantity}
                    >
                      {totalQuantity}
                    </motion.span>
                  </Link>
                </motion.div>
              )}
              
              {/* Admin panel icon */}
              {isAdminLoggedIn && (
                <motion.div 
                  variants={iconButtonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Link 
                    to="/admin"
                    className="p-2 text-gray-700 hover:text-[#406580] transition-colors"
                    aria-label="Admin panel"
                  >
                    <MdOutlineAdminPanelSettings className="text-2xl" />
                  </Link>
                </motion.div>
              )}
            </div>
          </nav>
        )}

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && width < breakpoint && (
            <motion.div 
              initial="closed"
              animate="open"
              exit="closed"
              variants={mobileNavVariants}
              className="fixed inset-y-0 right-0 w-full max-w-xs bg-white shadow-2xl z-10 overflow-y-auto"
            >
              <div className="p-6 h-full flex flex-col">
                <div className="flex justify-end">
                  <motion.button
                    variants={iconButtonVariants}
                    whileHover="hover"
                    whileTap="tap"
                    onClick={() => setIsOpen(false)}
                    className="p-2 text-gray-500 hover:text-gray-700"
                  >
                    <IoClose size={24} />
                  </motion.button>
                </div>
                
                <nav className="mt-8 flex-1">
                  <div className="space-y-4">
                    {buttons.map((button, i) => (
                      <motion.div 
                        key={button.id}
                        custom={i}
                        variants={mobileItemVariants}
                        className="py-2 border-b border-gray-100"
                      >
                        {button.link.startsWith('#') ? (
                          <a 
                            href={button.link} 
                            onClick={() => setIsOpen(false)}
                            className="block font-medium text-gray-800 hover:text-[#406580] transition-colors"
                          >
                            {button.name}
                          </a>
                        ) : (
                          <Link 
                            to={button.link} 
                            onClick={() => setIsOpen(false)}
                            className="block font-medium text-gray-800 hover:text-[#406580] transition-colors"
                          >
                            {button.name}
                          </Link>
                        )}
                      </motion.div>
                    ))}

                    {!isUserLoggedIn ? (
                      <div className="pt-4 space-y-4">
                        <motion.div variants={mobileItemVariants}>
                          <Link 
                            to="/login" 
                            onClick={() => setIsOpen(false)}
                            className="block w-full py-2 text-center font-medium text-[#406580] border border-[#406580] rounded-lg hover:bg-[#406580] hover:text-white transition-colors"
                          >
                            Login
                          </Link>
                        </motion.div>
                        <motion.div variants={mobileItemVariants}>
                          <Link 
                            to="/register" 
                            onClick={() => setIsOpen(false)}
                            className="block w-full py-2 text-center font-medium text-white bg-[#406580] rounded-lg hover:bg-[#305570] transition-colors"
                          >
                            Register
                          </Link>
                        </motion.div>
                      </div>
                    ) : (
                      <motion.div variants={mobileItemVariants} className="pt-4">
                        <Link 
                          to="/profilo" 
                          onClick={() => setIsOpen(false)}
                          className="flex items-center space-x-2 font-medium text-gray-800 hover:text-[#406580] transition-colors"
                        >
                          <FaRegUser />
                          <span>Il mio profilo</span>
                        </Link>
                      </motion.div>
                    )}

                    {cart && cart.length > 0 && (
                      <motion.div variants={mobileItemVariants} className="pt-2">
                        <Link 
                          to="/cart" 
                          onClick={() => setIsOpen(false)}
                          className="flex items-center space-x-2 font-medium text-gray-800 hover:text-[#406580] transition-colors"
                        >
                          <div className="relative">
                            <FaCartShopping />
                            <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 bg-red-500 text-white text-xs font-bold rounded-full">
                              {totalQuantity}
                            </span>
                          </div>
                          <span>Carrello</span>
                        </Link>
                      </motion.div>
                    )}
                  </div>
                </nav>
                
                <motion.div 
                  variants={mobileItemVariants}
                  className="mt-auto py-4 border-t border-gray-100 text-center text-sm text-gray-500"
                >
                  &copy; {new Date().getFullYear()} Marrocco Group
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}